import { NavLink, useParams } from 'react-router-dom';
import useFetch from '../../../api/useFetch';
import Header from '../../../components/special/Header';
import { useTranslation } from 'react-i18next';
import { ProjectManagementWorkPackageSkeleton } from './ProjectManagementWorkPackageSkeleton';
import CardGrid from '../../../components/cards/layout/CardGrid';
import CardContainer from '../../../components/cards/layout/CardContainer';
import RenderHTML from '../../../components/preview/RenderHTML';
import { ProjectUserStack } from '../../../components/exo/project/ProjectUserStack';
import {
  Box,
  Breadcrumbs,
  Button,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { CircularProgressbarWithChildren } from 'react-circular-progressbar';
import { tokens } from '../../../global/theme/tokens';
import { useTimeTracking } from '../../../components/time-tracking/AppTimeProvider';
import { renderHours } from '../../time-tracking/worktime';
import { WPEMap } from './work-package-elements/WPEMap';
import { createElement, useEffect, useRef, useState } from 'react';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import { API_DELETE, API_PATCH, DBRequest, useConfig } from '../../../api/api';
import { DragDropContext } from 'react-beautiful-dnd';
import { CHROverview } from '../../../components/exo/project/CHROverview';
import ExoAvatar from '../../../components/exo/ExoAvatar';
import WpTimeTrackingUserCard from './WpTimeTrackingUserCard';
import Cookies from 'universal-cookie';
import LayersIcon from '@mui/icons-material/Layers';
import StopIcon from '@mui/icons-material/Stop';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AssessmentIcon from '@mui/icons-material/Assessment';
import DateLabel from '../../../components/label/DateLabel';
import moment from 'moment';
import { Toolbar } from '../../../components/exo/project/Toolbar';
import SettingsApplicationsIcon from '@mui/icons-material/SettingsApplications';
import { WorkPackageForm } from '../../../components/exo/project/workPackage/WorkPackageForm';
import { updateOrCreate } from '../../../components/special/updateOrCreate';

export const ProjectManagementWorkPackage = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();

  const cookies = new Cookies();
  const cookieWorkPackageView =
    cookies.get('workPackage-view') || 'productivity';

  // toolbar
  const [wpFormOpen, setWPFormOpen] = useState(false);
  const toollist = [
    {
      Icon: SettingsApplicationsIcon,
      label: t('Settings'),
      onClick: () => setWPFormOpen(true),
    },
  ];

  const { clockIn, clockOut, activeProject, secondsIn } = useTimeTracking();

  const { pid, wid } = useParams();
  const { apiData, setApiData } = useFetch(`work-packages/${wid}`);
  const [project, setProject] = useState(null);
  const [wpTimeTrackingData, setWpTimeTrackingData] = useState(null);

  const [isDragging, setIsDragging] = useState(false);

  const [dropIndicator, setDropIndicator] = useState({
    droppableId: null,
    index: null,
  });

  const refs = useRef({});

  // reverse scrolling for time tracking log
  const scrollRef = useRef(null);
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [wpTimeTrackingData]);

  // view
  const [activeView, setActiveView] = useState(cookieWorkPackageView);
  function changeView(e, value) {
    if (activeView !== value && value) {
      setActiveView(value);
      cookies.set('workPackage-view', value);
    }
  }

  useEffect(() => {
    DBRequest({
      config,
      path: `projects/${pid}`,
      onResponse: (res) => setProject(res.data),
    });

    DBRequest({
      config,
      path: `work-packages/${wid}/time-tracking`,
      onResponse: (res) => setWpTimeTrackingData(res),
    });
  }, [config]);

  if (!apiData) return <ProjectManagementWorkPackageSkeleton />;

  const workPackage = apiData.data;

  const activeWorkpackage =
    activeProject &&
    activeProject.activeWorkPackage &&
    activeProject.activeWorkPackage.id === workPackage.id
      ? {
          ...activeProject.activeWorkPackage,
          totalTimeWorkedSeconds: secondsIn
            ? activeProject.activeWorkPackage.totalTimeWorkedSeconds + secondsIn
            : activeProject.activeWorkPackage.totalTimeWorkedSeconds,
        }
      : null;

  const workPercentage = activeWorkpackage
    ? activeWorkpackage.totalTimeWorkedSeconds / activeWorkpackage.estimatedTime
    : workPackage.totalTimeWorkedSeconds / workPackage.estimatedTime;

  // changes
  function handleChange(updatedElement) {
    const newElements = apiData.data.elements.map((obj) =>
      obj.id === updatedElement.id ? updatedElement : obj
    );
    setApiData({
      ...apiData,
      data: { ...apiData.data, elements: newElements },
    });
  }

  const handleOnDragStart = (result) => {
    setIsDragging(true);
  };

  const handleOnDragEnd = (result) => {
    const { source, destination } = result;
    // If item was dropped outside of a droppable, do nothing
    if (!destination) {
      setIsDragging(false);
      return;
    }

    // Build taskLists from all refs
    const taskLists = {};
    Object.keys(refs).forEach((el) => {
      if (el !== 'current') {
        taskLists[el] = refs[el].apiData?.data.tasks;
      }
    });

    const taskId = Number(result.draggableId);

    // Find the dragged task
    let task = null;
    Object.keys(taskLists).forEach((el) => {
      taskLists[el].forEach((taskItem) => {
        if (taskItem.id === taskId) {
          task = { ...taskItem, wpId: el };
        }
      });
    });

    // Handle delete
    if (destination.droppableId.includes('trash')) {
      handleDelete(task, taskLists);
    }

    // Handle normal task movement
    if (destination.droppableId.includes('tasks')) {
      const sourceWpeId = Number(source.droppableId.split(' ')[1]);
      const destWpeId = Number(destination.droppableId.split(' ')[1]);

      const isFilterActive = refs[sourceWpeId]?.isFilterActive ?? false;

      // BLOCK same-list reordering when filter/sort is active
      if (
        isFilterActive &&
        sourceWpeId === destWpeId // same list
      ) {
        setIsDragging(false);
        return;
      }

      // If no actual movement
      if (
        destination.droppableId === source.droppableId &&
        destination.index === source.index
      )
        return;

      // Compute target sort index
      let destSortIndex;
      if (taskLists[destWpeId].length > 0) {
        if (taskLists[destWpeId].length === destination.index) {
          destSortIndex =
            taskLists[destWpeId][destination.index - 1].sortIndex + 1;
        } else {
          destSortIndex = taskLists[destWpeId][destination.index].sortIndex;
        }
      } else {
        destSortIndex = 0;
      }

      // Always send backend request (moving across lists OR filtered drag)
      DBRequest({
        config,
        path: `work-packages/elements/list/tasks/${task.id}/move`,
        method: API_PATCH({ sortIndex: destSortIndex, listId: destWpeId }),
        onResponse: () => {},
      });

      // SHIFT SOURCE LIST
      let updatedSourceList = taskLists[task.wpId];
      updatedSourceList = updatedSourceList.map((el) => ({
        ...el,
        sortIndex:
          el.sortIndex > task.sortIndex ? el.sortIndex - 1 : el.sortIndex,
      }));

      //remove task from source List
      updatedSourceList = updatedSourceList.filter((el) => el.id !== task.id);

      //Set destination List
      let updatedDestList =
        destination.droppableId === source.droppableId
          ? updatedSourceList
          : taskLists[destWpeId];

      //shift destination List
      updatedDestList = updatedDestList.map((el) => ({
        ...el,
        sortIndex:
          el.sortIndex >= destSortIndex ? el.sortIndex + 1 : el.sortIndex,
      }));

      //add task to destinationList
      updatedDestList.push({ ...task, sortIndex: destSortIndex });

      // APPLY SOURCE LIST (when moving across lists)
      if (destination.droppableId !== source.droppableId) {
        updatedSourceList = updatedSourceList.sort(
          (a, b) => a.sortIndex - b.sortIndex
        );

        refs[task.wpId].setApiData({
          ...refs[task.wpId].apiData,
          data: {
            ...refs[task.wpId].apiData.data,
            tasks: updatedSourceList,
          },
        });
      }

      //apply destination list
      updatedDestList = updatedDestList.sort(
        (a, b) => a.sortIndex - b.sortIndex
      );

      refs[destWpeId].setApiData({
        ...refs[destWpeId].apiData,
        data: { ...refs[destWpeId].apiData.data, tasks: updatedDestList },
      });
    }

    setIsDragging(false);
  };

  function handleDelete(task, taskLists) {
    DBRequest({
      config,
      path: `work-packages/elements/list/tasks/${task.id}`,
      method: API_DELETE,
      onResponse: () => {},
    });
    let updatedTasks = taskLists[task.wpId].filter((el) => el.id !== task.id);
    updatedTasks = updatedTasks.map((el) => ({
      ...el,
      sortIndex:
        el.sortIndex > task.sortIndex ? el.sortIndex - 1 : el.sortIndex,
    }));

    refs[task.wpId].setApiData({
      ...refs[task.wpId].apiData,
      data: {
        ...refs[task.wpId].apiData.data,

        tasks: updatedTasks,
      },
    });
  }

  function handleWPDataChange(data) {
    setApiData((prev) => ({
      ...prev,
      data: {
        ...prev.data,
        ...data,
      },
    }));
  }

  /*const onDragUpdate = (update) => {
    const destination = update?.destination;

    if (destination) {
      setDropIndicator({
        droppableId: destination.droppableId,
        index: destination.index,
      });
    } else {
      setDropIndicator({ droppableId: null, index: null });
    }
  };*/

  const isAfterDeadline =
    workPackage?.status === 0 &&
    moment(workPackage?.endDate).isBefore(moment(), 'day');

  return (
    <>
      <Breadcrumbs aria-label="breadcrumb">
        <NavLink
          to={'/projects'}
          underline="hover"
          className="flex items-center"
          color="inherit"
        >
          <AccountTreeIcon className="mr-2" fontSize="inherit" />
          {t('Projects')}
        </NavLink>
        <NavLink
          to={`/projects/management/${pid}`}
          underline="hover"
          className="flex items-center"
          color="inherit"
        >
          {project && project.image && (
            <img
              className=" h-5 w-5 object-cover mr-2 rounded-full"
              src={project.image.temporaryUrl}
            />
          )}
          {project && project.name}
        </NavLink>
        <Typography
          sx={{ color: 'text.primary' }}
          className="flex items-center"
        >
          {apiData && apiData.data.icon && (
            <img
              className=" h-7 w-7 object-cover rounded-full"
              src={apiData.data.icon.temporaryUrl}
            />
          )}
          {apiData && apiData.data.title}
        </Typography>
      </Breadcrumbs>
      <Box className="flex gap-0 md:gap-8 md:items-center justify-between md:justify-start pr-2 md:flex-row flex-col pb-2">
        <Header title={workPackage.title} />
        <ToggleButtonGroup
          size="small"
          value={activeView}
          exclusive
          onChange={changeView}
          aria-label="Project Status"
          className="backdrop-blur-lg"
        >
          <Tooltip title={t('Productivity')}>
            <ToggleButton value="productivity">
              <LayersIcon />
            </ToggleButton>
          </Tooltip>
          <Tooltip title={t('Timeline')}>
            <ToggleButton value="timeline">
              <AssessmentIcon />
            </ToggleButton>
          </Tooltip>
        </ToggleButtonGroup>
      </Box>
      {project && project?.editable && (
        <Box className="pb-4">
          <Toolbar tools={toollist} hidden={true} />
        </Box>
      )}

      <WorkPackageForm
        phaseId={apiData?.data?.projectPhase?.id}
        open={wpFormOpen}
        onClose={() => {
          setWPFormOpen(false);
        }}
        onChange={handleWPDataChange}
        startDataCollection={apiData?.data}
        projectId={project?.id}
      />

      {activeView === 'productivity' && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            gap: 2,
            width: '100%',
            height: '100%',
          }}
        >
          {workPackage?.elements?.length > 0 && (
            <Box
              sx={{
                width: { xs: '100%', md: '75%' },
                order: { xs: 1, md: 0 }, // Goes on top in mobile, left on desktop
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <DragDropContext
                onDragEnd={handleOnDragEnd}
                onDragStart={handleOnDragStart}
              >
                {workPackage.elements.map((element) =>
                  createElement(WPEMap[element.type], {
                    element,
                    key: element.id,
                    pid,
                    onChange: handleChange,
                    isDragging,
                    ref: (el) => (refs[element.id] = el),
                  })
                )}
              </DragDropContext>
            </Box>
          )}

          <Box
            sx={{
              width: { xs: '100%', md: '25%' },
              order: { xs: 2, md: 1 }, // Goes below DragDrop in mobile
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {/* --- Progress & Time --- */}
            <CardContainer backgroundColor={colors.glass}>
              {Boolean(workPackage?.startDate && workPackage?.endDate) && (
                <Typography
                  className="absolute top-1 right-2 opacity-50"
                  fontSize={11}
                  fontWeight={600}
                  sx={{
                    color: isAfterDeadline ? colors.error : colors.text,
                  }}
                >
                  <DateLabel date={workPackage.startDate} /> -{' '}
                  <DateLabel date={workPackage.endDate} />
                </Typography>
              )}
              {workPackage.estimatedTime && (
                <Box sx={{ maxWidth: '80px', marginTop: '15px' }}>
                  <CircularProgressbarWithChildren
                    value={workPercentage}
                    maxValue={1}
                    styles={{
                      path: {
                        stroke:
                          workPercentage < 1
                            ? colors.primary[500]
                            : colors.redAccent[500],
                      },
                      trail: {
                        stroke: 'rgba(0, 0, 0, 0.2)',
                        transform: 'rotate(0.25turn)',
                      },
                    }}
                  >
                    <Typography variant="h4">
                      {Math.round(workPercentage * 100)}%
                    </Typography>
                  </CircularProgressbarWithChildren>
                </Box>
              )}

              <Box className="flex flex-col items-center gap-3 w-full">
                {/* Worked + Estimated (row) */}
                <Box className="flex items-center justify-between gap-3">
                  <Box className="flex flex-col items-center w-full">
                    <Typography variant="h6" className="pt-2 uppercase">
                      {t('Worked')}
                    </Typography>
                    <Typography variant="h4">
                      {renderHours(
                        (activeWorkpackage
                          ? activeWorkpackage.totalTimeWorkedSeconds
                          : workPackage.totalTimeWorkedSeconds) / 60
                      )}
                      h
                    </Typography>
                  </Box>

                  {workPackage.estimatedTime && (
                    <Box className="flex flex-col items-center w-full">
                      <Typography
                        variant="h6"
                        className="pt-2 uppercase"
                        noWrap
                      >
                        {t('Estimated')}
                      </Typography>
                      <Typography variant="h4">
                        {renderHours(
                          (activeWorkpackage
                            ? activeWorkpackage.estimatedTime
                            : workPackage.estimatedTime) / 60
                        )}
                        h
                      </Typography>
                    </Box>
                  )}
                </Box>

                {/* Clock In / Clock Out (below row, full width) */}
                {Boolean(workPackage?.isTimeTrackable) &&
                  workPackage?.allowTimeTracking && (
                    <Box className="flex justify-center pt-2">
                      {activeWorkpackage ? (
                        <Button
                          onClick={() => clockOut()}
                          className="w-full text-xl"
                          variant="outlined"
                          color="error"
                        >
                          <StopIcon /> &nbsp; {t('Clock Out')}
                        </Button>
                      ) : (
                        <Button
                          onClick={() => clockIn(project, workPackage)}
                          className="w-full text-xl"
                          variant="outlined"
                          color="success"
                        >
                          <PlayArrowIcon /> &nbsp; {t('Clock In')}
                        </Button>
                      )}
                    </Box>
                  )}
              </Box>
            </CardContainer>

            {/* --- Description --- */}
            {workPackage?.description && (
              <CardContainer backgroundColor={colors.glass}>
                <RenderHTML HTML={workPackage.description} />
              </CardContainer>
            )}

            {/* --- Users --- */}
            <CardContainer backgroundColor={colors.glass}>
              <ProjectUserStack
                workPackage={workPackage}
                users={workPackage.users}
              />
            </CardContainer>
          </Box>
        </Box>
      )}
      {activeView === 'timeline' && (
        <Box
          sx={{
            flexDirection: { xs: 'column-reverse', md: 'row' },
            gap: 2,
            height: { xs: 'auto', md: 'calc(100% - 82px)' },
          }}
          className="flex w-full h-full"
        >
          <CardContainer
            className="w-full h-full flex flex-col items-start"
            sx={{
              justifyContent: 'start',

              width: { xs: '100%', md: '60%' },
              height: { xs: '60vh', md: '100%' },
              order: { xs: 1, md: 1 }, // mobile: top, desktop: left
            }}
            backgroundColor={colors.glass}
          >
            <Typography fontWeight={500} className="pt-1 opacity-60 w-full">
              {t('History')}
            </Typography>

            <Box
              className="flex flex-col gap-2 w-full max-h-full"
              sx={{
                overflowY: 'auto',
                overflowX: 'hidden',
                flexDirection: 'column-reverse',
              }}
              ref={scrollRef}
            >
              {wpTimeTrackingData?.data.length === 0 ? (
                <Typography
                  fontSize="14px"
                  sx={{
                    opacity: 0.7,
                    textAlign: 'center',
                    width: '100%',
                    py: 2,
                  }}
                >
                  {t('No work has been tracked for this work package yet.')}
                </Typography>
              ) : (
                wpTimeTrackingData?.data.map((userData) => (
                  <WpTimeTrackingUserCard
                    key={userData.id}
                    userData={userData}
                  />
                ))
              )}
            </Box>
          </CardContainer>

          <Box
            sx={{
              width: { xs: '100%', md: '40%' },
              order: { xs: 2, md: 2 }, // mobile: bottom, desktop: right
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            {workPackage.author && (
              <CardContainer backgroundColor={colors.glass}>
                <Typography variant="h6" className="pt-2 uppercase">
                  {t('Author')}
                </Typography>
                <ExoAvatar
                  type="internal"
                  picture={
                    workPackage?.author?.picture?.temporaryUrl ??
                    workPackage?.author?.picture?.thumbnailUrl
                  }
                  name={`${workPackage?.author?.salutation} ${workPackage?.author?.firstName} ${workPackage?.author?.lastName}`}
                />
                <Typography>
                  {`${workPackage?.author?.salutation} ${workPackage?.author?.firstName} ${workPackage?.author?.lastName}`}
                </Typography>
              </CardContainer>
            )}

            {workPackage.wagePresets?.length > 0 && (
              <CardContainer backgroundColor={colors.glass}>
                <CHROverview
                  data={{ data: workPackage.wagePresets ?? [] }}
                  onlyTotal
                  maxHeight="800px"
                  noBackground
                />
              </CardContainer>
            )}
          </Box>
        </Box>
      )}
    </>
  );
};
